import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Ticket as TicketIcon, 
  Calendar, 
  ArrowRight, 
  Clock, 
  MapPin, 
  CheckCircle, 
  XCircle,
  Share2,
  RotateCcw,
  X,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { optimizedImageUrl, responsiveImageSrcSet } from "@/lib/images";
import {
  formatDateMMDDYear,
  formatEventLocal,
  isStandardRefundRequestAvailable,
} from "@/lib/eventTime";

// Share Modal Component
const ShareModal = ({ isOpen, onClose, eventTitle, eventUrl }) => {
  if (!isOpen) return null;

  const shareLinks = [
    {
      name: "X (Twitter)",
      icon: "𝕏",
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${eventTitle} on ShowMeLive!`)}&url=${encodeURIComponent(eventUrl)}`
    },
    {
      name: "Facebook",
      icon: "f",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`
    },
    {
      name: "WhatsApp",
      icon: "📱",
      url: `https://wa.me/?text=${encodeURIComponent(`Check out ${eventTitle}! ${eventUrl}`)}`
    }
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(eventUrl);
    toast.success("Link copied to clipboard!");
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-xl font-bold">Share This Show</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="space-y-3">
          {shareLinks.map((link) => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 w-full bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-lg transition-colors"
            >
              <span className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-lg">
                {link.icon}
              </span>
              <span>{link.name}</span>
            </a>
          ))}
          
          <button
            onClick={copyLink}
            className="flex items-center gap-3 w-full bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors"
          >
            <span className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              🔗
            </span>
            <span>Copy Link</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const MyTickets = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [shareModal, setShareModal] = useState({ isOpen: false, eventTitle: "", eventUrl: "" });
  const [refundBusy, setRefundBusy] = useState(null);
  const [refundClock, setRefundClock] = useState(() => new Date());

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setRefundClock(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await axiosInstance.get("/tickets");
      const ticketsData = response.data;
      setTickets(ticketsData);

      // Fetch event details for each ticket
      const eventIds = [...new Set(ticketsData.map(t => t.event_id))];
      const eventPromises = eventIds.map(id => 
        axiosInstance.get(`/events/${id}`).catch(() => null)
      );
      const eventResponses = await Promise.all(eventPromises);
      
      const eventsMap = {};
      eventResponses.forEach(res => {
        if (res && res.data) {
          eventsMap[res.data.id] = res.data;
        }
      });
      setEvents(eventsMap);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  // Handle calendar download for mobile compatibility
  const handleAddToCalendar = async (ticketId, eventTitle) => {
    try {
      const response = await fetch(
        `/api/tickets/${ticketId}/calendar`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate calendar file');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and click it
      const link = document.createElement('a');
      link.href = url;
      link.download = `${eventTitle.replace(/[^a-z0-9]/gi, '_')}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Calendar file downloaded!");
    } catch (error) {
      console.error("Calendar download error:", error);
      toast.error("Failed to download calendar file");
    }
  };

  const handleDeleteTicket = async (ticketId, eventTitle, eventStatus) => {
    if (eventStatus === "live") {
      toast.error("Cannot delete ticket for a live event");
      return;
    }
    
    const confirmed = window.confirm(
      `Are you sure you want to delete your ticket for "${eventTitle}"?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`/tickets/${ticketId}`);
      toast.success("Ticket deleted successfully");
      fetchTickets(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete ticket");
    }
  };

  const handleRefundRequest = async (ticket) => {
    const reason = window.prompt("Tell us why you are requesting a refund:", "Unable to attend the event");
    if (reason === null) return;
    setRefundBusy(ticket.id);
    try {
      await axiosInstance.post(`/tickets/${ticket.id}/refund-request`, { reason });
      toast.success("Refund request sent. The amount is held while an admin reviews it.");
      await fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request refund");
    } finally {
      setRefundBusy(null);
    }
  };

  // Navigate to watch event
  const handleViewEvent = (eventId) => {
    navigate(`/event/${eventId}`);
  };

  // Share event
  const handleShare = (event) => {
    const eventUrl = `${window.location.origin}/event/${event.id}`;
    setShareModal({
      isOpen: true,
      eventTitle: event.title,
      eventUrl: eventUrl
    });
  };

  // Get status badge for event
  const getStatusBadge = (event) => {
    if (!event) return null;
    
    switch (event.status) {
      case "live":
        return (
          <div className="flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white shadow-lg">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            LIVE NOW
          </div>
        );
      case "completed":
        return (
          <div className="flex items-center gap-2 rounded-full bg-slate-600/95 px-4 py-2 text-sm font-bold text-white shadow-lg backdrop-blur">
            <CheckCircle className="h-4 w-4" />
            Ended
          </div>
        );
      case "cancelled":
        return (
          <div className="flex items-center gap-2 rounded-full bg-red-800/95 px-4 py-2 text-sm font-bold text-white shadow-lg">
            <XCircle className="h-4 w-4" />
            Cancelled
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 rounded-full bg-green-600/95 px-4 py-2 text-sm font-bold text-white shadow-lg">
            <Clock className="h-4 w-4" />
            Upcoming
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090909]" data-testid="my-tickets-page">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="mx-auto max-w-[760px] px-4 pb-24 pt-28 sm:px-7">
        <h1 className="mb-9 text-center text-4xl font-black tracking-tight text-white sm:text-5xl" data-testid="tickets-title">
          My Tickets
        </h1>
        
        {tickets.length === 0 ? (
          <div className="text-center py-20" data-testid="no-tickets-message">
            <TicketIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-xl mb-4">You haven't purchased any tickets yet</p>
            <Button 
              onClick={() => navigate("/events")}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Browse Events
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {tickets.map((ticket) => {
              const event = events[ticket.event_id];
              const isLive = event?.status === "live";
              const isCancelled = event?.status === "cancelled";
              const isRefunded = ticket.refunded;
              const standardRefundAvailable = isStandardRefundRequestAvailable(event, refundClock);
              
              // Check if event date has passed
              let isPast = false;
              if (event?.date) {
                const eventDate = new Date(event.date);
                isPast = eventDate < new Date();
              }
              
              return (
                <div 
                  key={ticket.id} 
                  className={`overflow-hidden rounded-[28px] border-2 bg-[#0c162b] shadow-[0_24px_70px_rgba(0,0,0,.38)] ${
                    isLive 
                      ? "border-red-500 shadow-red-950/30"
                      : isRefunded || isCancelled
                      ? "border-gray-700 opacity-70"
                      : "border-[#1768ff]"
                  }`}
                  data-testid={`ticket-card-${ticket.id}`}
                >
                  {/* Event Image with Badges */}
                  <div className="relative">
                    <img 
                      src={optimizedImageUrl(event?.image_url || "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400", 640)} 
                      srcSet={responsiveImageSrcSet(event?.image_url || "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400", [320, 480, 640])}
                      sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                      alt={event?.title || "Event"}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[4/3] w-full object-cover"
                    />
                    
                    {/* Status badge - Top Left */}
                    <div className="absolute left-4 top-4">
                      {getStatusBadge(event)}
                    </div>
                    
                    {/* Ticket quantity badge - Top Right */}
                    <div 
                      className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#1768ff] text-base font-black text-white shadow-lg"
                      data-testid={`ticket-quantity-${ticket.id}`}
                    >
                      x{ticket.quantity}
                    </div>
                    
                    {/* Refunded overlay */}
                    {isRefunded && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="bg-yellow-600 text-white px-6 py-3 rounded-lg font-bold text-lg">
                          REFUNDED
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Event Details */}
                  <div className="p-5 sm:p-7">
                    <h3 className="mb-4 text-2xl font-black tracking-tight text-white" data-testid={`ticket-event-title-${ticket.id}`}>
                      {event?.title || "Event Unavailable"}
                    </h3>
                    
                    {event && (
                      <div className="mb-5 space-y-3">
                        <div className="flex items-center gap-3 text-gray-300">
                          <Calendar className="h-5 w-5 shrink-0 text-gray-500" />
                          <span className="text-base" data-testid={`ticket-event-date-${ticket.id}`}>
                            {formatEventLocal(event)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-3 text-gray-300">
                          <MapPin className="h-5 w-5 shrink-0 text-gray-500" />
                          <span className="text-base" data-testid={`ticket-event-venue-${ticket.id}`}>
                            {event.venue || "Online"}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Purchase Info */}
                    <div className="space-y-3 border-t border-white/10 py-5">
                      <div className="flex justify-between items-center">
                        <span className="text-base text-gray-400">Purchased</span>
                        <span className="text-base text-white" data-testid={`ticket-purchase-date-${ticket.id}`}>
                          {formatDateMMDDYear(ticket.purchase_date)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-base text-gray-400">Amount Paid</span>
                        <span className={`text-xl font-black ${isRefunded ? "text-yellow-500 line-through" : "text-emerald-400"}`}>
                          ${ticket.amount_paid?.toFixed(2) || "0.00"}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    {!isRefunded && !isCancelled && (
                      <div className="space-y-3 pt-2">
                        {/* View Event Button - Green */}
                        <Button
                          onClick={() => handleViewEvent(event?.id)}
                          className="h-14 w-full rounded-xl bg-[#16a34a] text-base font-black text-white hover:bg-[#15803d]"
                          data-testid={`view-event-${ticket.id}`}
                        >
                          <ArrowRight className="w-5 h-5 mr-2" />
                          View Event
                        </Button>
                        
                        {/* Add to Calendar Button - Blue */}
                        <Button
                          onClick={() => handleAddToCalendar(ticket.id, event?.title || "Event")}
                          className="h-14 w-full rounded-xl bg-[#1768ed] text-base font-bold text-white hover:bg-[#1457ca]"
                          data-testid={`add-to-calendar-${ticket.id}`}
                        >
                          <Calendar className="w-5 h-5 mr-2" />
                          Add to Calendar
                        </Button>
                        
                        {/* Share This Show Button - Outlined */}
                        <Button
                          onClick={() => handleShare(event)}
                          variant="outline"
                          className="h-14 w-full rounded-xl border-white/25 bg-transparent text-base font-bold text-gray-200 hover:bg-white/5 hover:text-white"
                          data-testid={`share-event-${ticket.id}`}
                        >
                          <Share2 className="w-5 h-5 mr-2" />
                          Share This Show
                        </Button>

                        {Number(ticket.amount_paid || 0) > 0 && ticket.refund_status !== "pending" && standardRefundAvailable && (
                          <Button
                            onClick={() => handleRefundRequest(ticket)}
                            disabled={refundBusy === ticket.id}
                            variant="outline"
                            className="h-14 w-full rounded-xl border-yellow-600/50 bg-transparent text-base font-bold text-yellow-400 hover:bg-yellow-600/15"
                            data-testid={`request-refund-${ticket.id}`}
                          >
                            <RotateCcw className="w-5 h-5 mr-2" />
                            {refundBusy === ticket.id ? "Sending Request..." : "Request Refund"}
                          </Button>
                        )}
                        {Number(ticket.amount_paid || 0) > 0 && ticket.refund_status !== "pending" && !standardRefundAvailable && (
                          <p className="rounded-xl border border-white/10 bg-white/[.03] px-4 py-3 text-center text-sm text-gray-400">
                            The standard refund window has closed. During a live event, use Report to submit a violation and request an automatic refund.
                          </p>
                        )}
                      </div>
                    )}

                    {ticket.refund_status === "pending" && (
                      <div className="mt-3 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3 text-center text-sm text-yellow-400">
                        Refund pending admin review
                      </div>
                    )}

                    {/* Delete Ticket - always visible except for live events */}
                    {event?.status !== "live" && ticket.refund_status !== "pending" && (
                      <div className="pt-3">
                        <Button
                          onClick={() => handleDeleteTicket(ticket.id, event?.title || "Event", event?.status)}
                          variant="outline"
                          className="h-14 w-full rounded-xl border-red-600/70 bg-transparent text-base font-bold text-red-400 hover:bg-red-600/15 hover:text-red-300"
                          data-testid={`delete-ticket-${ticket.id}`}
                        >
                          <Trash2 className="w-5 h-5 mr-2" />
                          Delete Ticket
                        </Button>
                      </div>
                    )}
                    
                    {/* Refund info */}
                    {isRefunded && ticket.refund_reason && (
                      <div className="text-yellow-500 text-sm text-center p-3 bg-yellow-500/10 rounded-lg mt-3">
                        {ticket.refund_reason}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={() => setShareModal({ ...shareModal, isOpen: false })}
        eventTitle={shareModal.eventTitle}
        eventUrl={shareModal.eventUrl}
      />
    </div>
  );
};

export default MyTickets;
